import { useState, useEffect } from 'react';
import api, { FechamentoApi, RelatoriosApi, ColaboradoresApi } from '../api/client.js';
import { useQuery } from '@tanstack/react-query';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend, BarChart, Bar, XAxis, YAxis } from 'recharts';

export default function FechamentoPage() {
  const hoje = new Date();
  const [ano,setAno]=useState(hoje.getFullYear());
  const [mes,setMes]=useState(hoje.getMonth()+1);
  const [status,setStatus]=useState(null);
  const [loading,setLoading]=useState(false);
  const [apenasConsolidado,setApenasConsolidado]=useState(false);
  const downloadFile = async (url, filename) => {
    try {
      const response = await api.get(url, { responseType: 'blob' });
      const blob = response.data;
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Erro ao baixar arquivo:', err);
      alert(`Erro ao baixar arquivo: ${err.message}`);
    }
  };

  const executar = async () => { setLoading(true); try { const r = await FechamentoApi.executar(ano, mes); setStatus(r); refetchRelatorio(); } finally { setLoading(false); } };
  const consultar = async () => { setLoading(true); try { const r = await FechamentoApi.status(ano, mes); setStatus(r); } finally { setLoading(false); } };

  // Dados de colaboradores para nomes
  const { data: colabs } = useQuery({ queryKey:['colaboradores','fechamento'], queryFn: () => ColaboradoresApi.listar() });
  const nomePorId = new Map((Array.isArray(colabs)? colabs: (colabs?.data||[])).map(c=> [c.id, c.usuario?.nome || c.usuario?.email || c.id]));

  // Relatório agregado
  const { data: relatorio, refetch: refetchRelatorio, isFetching: loadingRel } = useQuery({
    queryKey:['rel-fechamento-geral', ano, mes, apenasConsolidado],
    queryFn: () => RelatoriosApi.fechamentoGeral(ano, mes, { consolidado: apenasConsolidado }),
  });

  useEffect(()=>{ consultar(); refetchRelatorio(); },[]); // load initial

  const pieData = relatorio ? [
    { name:'Pontos', value: relatorio.totalPontos },
    { name:'Valor', value: relatorio.totalValor }
  ]: [];
  const barData = relatorio ? relatorio.colaboradores.map(c => ({
    nome: (nomePorId.get(c.colaboradorId) || c.colaboradorId).slice(0,14)+( (nomePorId.get(c.colaboradorId)||'').length>14?'…':''),
    pontos: c.pontos,
    valor: c.valor
  })) : [];
  const pieColors = ['#006bb6','#0B8457'];
  return (
    <div>
      <h2 className="page-title">Fechamento Mensal</h2>
      <div className="card flex gap-sm" style={{alignItems:'flex-end', flexWrap:'wrap', marginBottom:'1rem'}}>
        <div>
          <label>Ano</label>
          <input type="number" value={ano} onChange={e=>setAno(e.target.value)} style={{width:'100px'}} />
        </div>
        <div>
          <label>Mês</label>
          <input type="number" value={mes} min={1} max={12} onChange={e=>setMes(e.target.value)} style={{width:'70px'}} />
        </div>
        <label style={{display:'flex', alignItems:'center', gap:4, fontSize:'.7rem', fontWeight:600}}>
          <input type="checkbox" checked={apenasConsolidado} onChange={e=>{ setApenasConsolidado(e.target.checked); refetchRelatorio(); }} /> Apenas Consolidado
        </label>
        <button className="btn" onClick={executar} disabled={loading}>Executar</button>
        <button className="btn outline" onClick={consultar} disabled={loading}>Status</button>
        {relatorio && <div style={{marginLeft:'auto'}} className="flex gap-sm">
          <button className="btn outline" onClick={() => downloadFile(RelatoriosApi.fechamentoExcelUrl(ano, mes), `fechamento_${ano}_${String(mes).padStart(2,'0')}.xlsx`)}>Excel</button>
          <button className="btn outline" onClick={() => downloadFile(RelatoriosApi.fechamentoPdfUrl(ano, mes), `fechamento_${ano}_${String(mes).padStart(2,'0')}.pdf`)}>PDF</button>
        </div>}
      </div>
      <div className="flex gap-md" style={{flexWrap:'wrap'}}>
        {status && <div className="card" style={{flex:'1 1 260px', minWidth:'240px'}}>
          <h3 style={{marginTop:0, fontSize:'.8rem'}}>Status</h3>
          <table className="table" style={{fontSize:'.65rem'}}>
            <tbody>
              <tr><th style={{width:'55%'}}>Ano</th><td>{status.ano}</td></tr>
              <tr><th>Mês</th><td>{String(status.mes).padStart(2,'0')}</td></tr>
              <tr><th>Total Registros</th><td>{status.total}</td></tr>
              <tr><th>Pendentes</th><td>{status.pendentes}</td></tr>
              <tr><th>Consolidado</th><td>{status.consolidado? 'Sim':'Não'}</td></tr>
            </tbody>
          </table>
        </div>}
        {relatorio && <div className="card" style={{flex:'1 1 300px', minWidth:'260px'}}>
          <h3 style={{marginTop:0, fontSize:'.8rem'}}>Totais Mês</h3>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} label>
                {pieData.map((p,i)=>(<Cell key={p.name} fill={pieColors[i%pieColors.length]} />))}
              </Pie>
              <Legend verticalAlign="bottom" height={20} />
              <Tooltip formatter={(v,name)=> name==='Valor'? `R$ ${Number(v).toFixed(2)}`: v} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{fontSize:'.65rem', marginTop:'.5rem'}}>
            <div><strong>Total Pontos:</strong> {relatorio.totalPontos}</div>
            <div><strong>Total Valor:</strong> R$ {relatorio.totalValor.toFixed(2)}</div>
          </div>
  </div>}
  {relatorio && <div className="card" style={{flex:'2 1 400px', minWidth:'340px'}}>
          <h3 style={{marginTop:0, fontSize:'.8rem'}}>Por Colaborador</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={barData} margin={{top:10,right:10,left:0,bottom:0}}>
              <XAxis dataKey="nome" fontSize={10} interval={0} tickFormatter={v=> v.length>8? v.slice(0,8)+'…':v} />
              <YAxis fontSize={10} />
              <Tooltip formatter={(v,n)=> n==='valor'? `R$ ${Number(v).toFixed(2)}`: v} />
              <Legend wrapperStyle={{fontSize:10}} />
              <Bar dataKey="pontos" fill="var(--color-orange)" name="Pontos" />
              <Bar dataKey="valor" fill="var(--color-primary)" name="Valor (R$)" />
            </BarChart>
          </ResponsiveContainer>
          <div style={{overflowX:'auto'}}>
            <table className="table" style={{fontSize:'.65rem', marginTop:'.5rem'}}>
              <thead>
                <tr><th>Colaborador</th><th>Pontos</th><th>Valor (R$)</th><th>Registros</th></tr>
              </thead>
              <tbody>
                {relatorio.colaboradores.map(c=> (
                  <tr key={c.colaboradorId}>
                    <td>{nomePorId.get(c.colaboradorId) || c.colaboradorId}</td>
                    <td>{c.pontos}</td>
                    <td>{c.valor.toFixed(2)}</td>
                    <td>{c.registros}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>}
      </div>
    </div>
  );
}
